import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportThread, ISupportThreadAttachment } from '../../../models/Support/ISupportThread';
import { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { DeliveryStatus, ThreadType } from '../../../models/Support/SupportEnums';
import {
	formatFileSize,
	getInitials,
	getSupportContactProfilePicSrc,
	getSupportUserProfilePicSrc,
} from '../../../utils/Support/supportHelpers';
import { Avatar } from '@HoudiniUI/Avatar';
import { Button } from '../../../web/components-v2/Button';
import { ImagePreviewDialog } from '../../../web/components-v2/ImagePreviewDialog';
import { Popover, PopoverAnchor, PopoverContent } from '../../../web/components-v2/Popover';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { styles } from './styles';
import { formatDateTime, getConfidenceInfo } from './supportTicketPresentation';
import { renderThreadBodyHtml } from './threadBodyHtml';

export interface ThreadItemProps {
	thread: ISupportThread;
	ticket?: ISupportTicket;
	agentNameMap?: Record<string, string>;
	agentProfilePicMap?: Record<string, string>;
	agentProfilePicEmailMap?: Record<string, string>;
	currentUserId?: string;
	currentUserName?: string;
	currentUserEmail?: string;
	currentUserProfilePic?: string;
	currentAccountId?: string;
	onAcceptDraft?: (body: string, threadId?: string) => void;
	onRejectDraft?: (threadId: string) => void;
	draftStatus?: 'pending' | 'accepted' | 'rejected';
	/** Latest thread shows full body by default; older messages stay trimmed. */
	isLatestThread?: boolean;
}

const buildThreadMentions = (
	mentionedUserIds: string[] | undefined,
	agentNameMap: Record<string, string>,
	currentUserId: string | undefined,
	currentUserName: string | undefined
) =>
	(mentionedUserIds ?? [])
		.map(userId => {
			const label = agentNameMap[userId] || (userId === currentUserId ? currentUserName : undefined);
			return label ? { label, userId } : undefined;
		})
		.filter((mention): mention is { label: string; userId: string } => !!mention);

export const ThreadItem: React.FC<ThreadItemProps> = ({
	thread,
	ticket,
	agentNameMap = {},
	agentProfilePicMap = {},
	agentProfilePicEmailMap = {},
	currentUserId,
	currentUserName,
	currentUserEmail,
	currentUserProfilePic,
	currentAccountId,
	onAcceptDraft,
	onRejectDraft,
	draftStatus,
	isLatestThread,
}) => {
	const isCustomer = thread.type === ThreadType.CustomerMessage;
	const isNote = thread.type === ThreadType.Note;
	const isAiDraft = thread.type === ThreadType.AiDraft;
	const isAgentAuthored = thread.type === ThreadType.AgentReply || isNote || thread.type === ThreadType.Forward;
	const deliveryFailed =
		thread.deliveryStatus === DeliveryStatus.Bounced || thread.deliveryStatus === DeliveryStatus.Dropped;
	const [collapsed, setCollapsed] = React.useState(isAiDraft && draftStatus !== 'pending');

	const hasTrimmedVersion = thread.bodyTrimmed && thread.bodyTrimmed !== thread.body;
	const [showFull, setShowFull] = React.useState(!!isLatestThread);
	const [previewImage, setPreviewImage] = React.useState<{ src: string; alt: string; fileName?: string } | null>(null);
	const wasLatestThreadRef = React.useRef(!!isLatestThread);

	React.useEffect(() => {
		if (isLatestThread) {
			setShowFull(true);
		} else if (wasLatestThreadRef.current) {
			setShowFull(false);
		}
		wasLatestThreadRef.current = !!isLatestThread;
	}, [isLatestThread]);

	const openImagePreview = (target: EventTarget | null) => {
		if (!(target instanceof HTMLImageElement)) return false;
		const src = target.currentSrc || target.src;
		if (!src) return false;
		setPreviewImage({ src, alt: target.alt || 'Email image' });
		return true;
	};
	const handleThreadBodyClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (openImagePreview(event.target)) event.preventDefault();
	};
	const handleThreadBodyKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		if (openImagePreview(event.target)) event.preventDefault();
	};

	if (isAiDraft && draftStatus !== 'pending' && collapsed) {
		return (
			<div
				data-thread-id={thread.id}
				className={css(styles.thread, styles.threadSystem, styles.threadSystemClickable)}
				onClick={() => setCollapsed(false)}
			>
				AI Draft ({draftStatus}) &middot; {formatDateTime(thread.creationDate)} &mdash; Click to expand
			</div>
		);
	}
	const threadStyle = isCustomer
		? styles.threadCustomer
		: isNote
			? styles.threadNote
			: isAiDraft
				? styles.threadAiDraft
				: styles.threadAgent;
	const avatarStyle = isCustomer
		? styles.threadAvatarCustomer
		: isNote
			? styles.threadAvatarNote
			: isAiDraft
				? styles.threadAvatarAiDraft
				: undefined;
	const displayBody = showFull ? thread.body || '' : thread.bodyTrimmed || thread.body || '';
	const bodyHtml = renderThreadBodyHtml(displayBody, {
		currentUserId,
		mentions: buildThreadMentions(thread.mentionedUserIds, agentNameMap, currentUserId, currentUserName),
	});
	const shouldPreWrap = !isCustomer && !displayBody.includes('<p>') && !displayBody.includes('<br');
	const resolvedAuthorName =
		thread.authorName || (isAgentAuthored && thread.authorId ? agentNameMap[thread.authorId] : undefined);
	const displayAuthorName = isAiDraft ? 'AI Assistant' : resolvedAuthorName || thread.authorEmail || 'Unknown sender';
	const currentUserEmailLower = currentUserEmail?.trim().toLowerCase();
	const isCurrentUserAuthor =
		(!!thread.authorId && thread.authorId === currentUserId) ||
		(!!thread.authorEmail &&
			!!currentUserEmailLower &&
			thread.authorEmail.trim().toLowerCase() === currentUserEmailLower);
	const authorEmailProfilePic = thread.authorEmail
		? agentProfilePicEmailMap[thread.authorEmail.trim().toLowerCase()]
		: undefined;
	const authorUserProfilePicSrc = getSupportUserProfilePicSrc({
		profilePic: isCurrentUserAuthor ? currentUserProfilePic : undefined,
		authorProfilePic: thread.authorProfilePic,
		agentProfilePic: (thread.authorId ? agentProfilePicMap[thread.authorId] : undefined) || authorEmailProfilePic,
		userId: thread.authorId || (isCurrentUserAuthor ? currentUserId : undefined),
		accountId: currentAccountId,
	});
	const avatarImgSrc = isAiDraft
		? undefined
		: isCustomer
			? authorUserProfilePicSrc || (ticket ? getSupportContactProfilePicSrc(ticket) : undefined)
			: authorUserProfilePicSrc;

	return (
		<div
			data-thread-id={thread.id}
			className={css(styles.thread, threadStyle, draftStatus === 'rejected' && styles.threadRejected)}
		>
			<div className={css(styles.threadHeader)}>
				<div className={css(styles.threadAuthorRow)}>
					<Avatar
						imgSrc={avatarImgSrc}
						altText={displayAuthorName}
						fallbackText={isAiDraft ? 'AI' : getInitials(displayAuthorName, thread.authorEmail)}
						smallText
						styles={[styles.threadAvatar, ...(avatarStyle ? [avatarStyle] : [])]}
					/>
					<div>
						<div className={css(styles.threadAuthorName)}>
							{displayAuthorName}
							{isNote && (
								<>
									{' '}
									<span className={css(styles.noteBadge)}>Note</span>
								</>
							)}
							{deliveryFailed && (
								<>
									{' '}
									<span
										className={css(styles.deliveryFailedBadge)}
										title={`This email was not delivered (${thread.deliveryStatus?.toLowerCase()}).`}
									>
										Couldn&#8217;t be delivered
									</span>
								</>
							)}
							{isAiDraft && (
								<>
									{' '}
									<span className={css(styles.aiDraftBadge)}>AI Draft</span>
									{draftStatus === 'accepted' && <span className={css(styles.draftAccepted)}> Accepted</span>}
									{draftStatus === 'rejected' && <span className={css(styles.draftRejectedLabel)}> Rejected</span>}
								</>
							)}
						</div>
						{isAiDraft && (
							<div className={css(styles.threadAuthorEmail)}>
								AI-generated draft &mdash; review before sending
								{ticket?.aiConfidence != null &&
									(() => {
										const info = getConfidenceInfo(ticket.aiConfidence);
										return (
											<span
												className={css(styles.aiScoreBadge, info.style, styles.aiScoreBadgeInline)}
												title={ticket?.confidenceReasoning || ticket?.aiReasoning || undefined}
											>
												Confidence: {info.label} ({ticket.aiConfidence})
											</span>
										);
									})()}
							</div>
						)}
						{!isAiDraft && resolvedAuthorName && thread.authorEmail && (
							<div className={css(styles.threadAuthorEmail)}>From {thread.authorEmail}</div>
						)}
						{thread.toEmails?.length > 0 && (
							<div className={css(styles.threadRecipients)}>To: {thread.toEmails.join(', ')}</div>
						)}
						{thread.ccEmails?.length > 0 && (
							<div className={css(styles.threadRecipients)}>CC: {thread.ccEmails.join(', ')}</div>
						)}
						{thread.bccEmails?.length > 0 && (
							<div className={css(styles.threadRecipients)}>BCC: {thread.bccEmails.join(', ')}</div>
						)}
					</div>
				</div>
				<div className={css(styles.threadTime)}>{formatDateTime(thread.creationDate)}</div>
			</div>
			{isAiDraft && draftStatus !== 'pending' ? (
				<div
					className={css(styles.collapsedToggle)}
					role='button'
					tabIndex={0}
					onClick={() => setCollapsed(true)}
					onKeyDown={e => {
						if (e.key === 'Enter' || e.key === ' ') {
							e.preventDefault();
							setCollapsed(true);
						}
					}}
				>
					Hide draft &#9650;
				</div>
			) : null}
			<div
				className={`${css(styles.threadBody, shouldPreWrap && styles.threadBodyPreWrap)} thread-body-html`}
				onClick={handleThreadBodyClick}
				onKeyDown={handleThreadBodyKeyDown}
				dangerouslySetInnerHTML={{ __html: bodyHtml }}
			/>
			{hasTrimmedVersion && (
				<Button variant='ghost' size='sm' styles={[styles.showFullToggle]} onClick={() => setShowFull(!showFull)}>
					{showFull ? '▲ Show less' : '▼ Show full email'}
				</Button>
			)}
			{thread.attachments && thread.attachments.length > 0 && (
				<div className={css(styles.threadAttachments)}>
					{thread.attachments.map((attachment: ISupportThreadAttachment) => (
						<ThreadAttachmentChip
							key={attachment.id}
							attachment={attachment}
							onPreview={(src, alt, fileName) => setPreviewImage({ src, alt, fileName })}
						/>
					))}
				</div>
			)}
			{isAiDraft && draftStatus === 'pending' && (
				<div className={css(styles.draftActions)}>
					<Button
						variant='primary'
						size='sm'
						onClick={() => onAcceptDraft?.(thread.body || thread.bodyPlainText, thread.id)}
					>
						Accept Draft
					</Button>
					<Button variant='outline' size='sm' onClick={() => onRejectDraft?.(thread.id)}>
						Reject
					</Button>
				</div>
			)}
			{previewImage && (
				<ImagePreviewDialog
					open
					src={previewImage.src}
					alt={previewImage.alt}
					fileName={previewImage.fileName}
					onOpenChange={open => !open && setPreviewImage(null)}
				/>
			)}
		</div>
	);
};

const IMAGE_FILE_NAME_PATTERN = /\.(avif|bmp|gif|jpe?g|png|svg|webp)$/i;

interface ThreadAttachmentChipProps {
	attachment: ISupportThreadAttachment;
	onPreview: (src: string, alt: string, fileName: string) => void;
}

const ThreadAttachmentChip: React.FC<ThreadAttachmentChipProps> = ({ attachment, onPreview }) => {
	const [hoverPreviewOpen, setHoverPreviewOpen] = React.useState(false);
	const url = attachment.url || attachment.storedFile?.publicUrl;
	const isImage = attachment.mimeType?.startsWith('image/') || IMAGE_FILE_NAME_PATTERN.test(attachment.fileName);
	const contents = (
		<>
			<span className={css(styles.attachmentIcon)}>📎</span>
			<span className={css(styles.attachmentName)}>{attachment.fileName}</span>
			<span className={css(styles.attachmentSize)}>{formatFileSize(attachment.fileSize)}</span>
		</>
	);

	if (!isImage || !url) {
		return (
			<a
				className={css(styles.attachmentChip)}
				href={url}
				target='_blank'
				rel='noopener noreferrer'
				title={attachment.fileName}
				onClick={event => {
					if (!url) event.preventDefault();
				}}
			>
				{contents}
			</a>
		);
	}

	return (
		<Popover open={hoverPreviewOpen} onOpenChange={setHoverPreviewOpen}>
			<PopoverAnchor asChild>
				<Button
					type='button'
					variant='outline'
					size='sm'
					styles={[styles.attachmentChip]}
					onMouseEnter={() => setHoverPreviewOpen(true)}
					onMouseLeave={() => setHoverPreviewOpen(false)}
					onFocus={() => setHoverPreviewOpen(true)}
					onBlur={() => setHoverPreviewOpen(false)}
					onClick={() => {
						setHoverPreviewOpen(false);
						onPreview(url, attachment.fileName, attachment.fileName);
					}}
				>
					{contents}
				</Button>
			</PopoverAnchor>
			<PopoverContent
				side='top'
				align='start'
				sideOffset={THEME.spacing.sm}
				styles={[styles.attachmentHoverPreview]}
				onOpenAutoFocus={event => event.preventDefault()}
				onCloseAutoFocus={event => event.preventDefault()}
			>
				<img className={css(styles.attachmentHoverPreviewImage)} src={url} alt={`${attachment.fileName} preview`} />
			</PopoverContent>
		</Popover>
	);
};
